import { v4 as uuid } from 'uuid'

import {
  apAnalytics,
  apLengthBucket,
  recordApVisit,
  registerApEvents,
  rememberApHandoff,
  takeApHandoff,
  type ApAttempt,
  type ApContext,
  type ApErrorCode,
  type ApIntents
} from '../apAnalytics'
import { registerApLegacyEvents } from '../apAnalyticsPrivacy'

export const overviewScreenId = 'ap_overview'

export const overviewDataSections = [
  'quick_actions',
  'unpaid_bills',
  'performance',
  'spend_by_category',
  'upcoming_bills',
  'approvals',
  'payment_method_usage',
  'top_vendors',
  'vendor_activity'
] as const

export const overviewSections = [
  ...overviewDataSections,
  'sidebar',
  'support'
] as const

export const overviewDestinations = [
  'bill_create',
  'bills',
  'bills_scheduled',
  'bills_needs_approval',
  'vendor_create',
  'vendor_details',
  'vendors',
  'vendor_duplicates',
  'bill_details',
  'payment_review',
  'pay_date_editor',
  'help_center',
  'support_widget',
  'other_product'
] as const

export const overviewActions = [
  'create_bill',
  'open_scheduled',
  'create_vendor',
  'open_vendor',
  'open_bills',
  'open_duplicates',
  'open_bill',
  'open_payment',
  'request_approval',
  'approve_and_pay',
  'update_pay_date',
  'open_forwarding_email'
] as const

const sectionStates = [
  'loading',
  'populated',
  'empty',
  'error',
  'unavailable'
] as const
const screenStates = ['populated', 'empty', 'partial', 'error'] as const
const billStates = [
  'draft',
  'needs_approval',
  'scheduled',
  'processing',
  'paid',
  'failed',
  'unknown'
] as const
const operations = [
  'navigation',
  'clipboard',
  'period_load',
  'import_acceptance',
  'approval_submission',
  'support',
  'upload',
  'section_load'
] as const
const errorClasses = [
  'validation',
  'permission',
  'conflict',
  'network',
  'service',
  'unknown'
] as const
const lastActions = [
  ...overviewActions,
  'period_changed',
  'email_copy',
  'upload',
  'approval_submit',
  'support',
  'menu'
] as const

export type OverviewSection = (typeof overviewSections)[number]
export type OverviewDestination = (typeof overviewDestinations)[number]
export type OverviewAction = (typeof overviewActions)[number]
export type OverviewSectionState = (typeof sectionStates)[number]
export type OverviewScreenState = (typeof screenStates)[number]
type Properties = Record<string, unknown>

registerApEvents({
  events: {
    'apOverview.screenViewed':
      'visitType entrySource loadDurationMs screenState availableSections unavailableSections restoredState',
    'apOverview.sectionStateChanged':
      'sectionId state previousState loadAttemptId requestId itemCount durationMs errorCode errorId countsComplete',
    'apOverview.newBillMenuChanged':
      'state reason isImporting availableActions',
    'apOverview.actionClicked':
      'sectionId action destinationId billId vendorId billState',
    'apOverview.navigationCompleted':
      'destinationId destinationVisitId durationMs sectionId billId vendorId',
    'apOverview.periodChanged':
      'sectionId previousPeriodDays periodDays loadAttemptId',
    'apOverview.emailCopyRequested': 'sectionId',
    'apOverview.emailCopyCompleted': 'outcome durationMs errorCode errorId',
    'apOverview.uploadSelectionStarted': 'uploadBatchId selectionMethod',
    'apOverview.uploadSelectionCanceled': 'uploadBatchId',
    'apOverview.uploadFilesSelected':
      'uploadBatchId selectedCount acceptedCount rejectedCount capacityDeferredCount uploadAttemptIds',
    'apOverview.uploadFileValidationCompleted':
      'uploadBatchId uploadAttemptId fileType sizeBucket outcome reason',
    'apOverview.uploadDispatched':
      'uploadBatchId uploadAttemptId requestId fileType sizeBucket',
    'apOverview.approvalDialogChanged': 'billId state sourceSection',
    'apOverview.approvalSubmitClicked': 'billId requestId sourceSection',
    'apOverview.paymentEntryBlocked':
      'billId sourceSection reason hasNextValidDate',
    'apOverview.paymentBlockDismissed':
      'billId sourceSection reason hasNextValidDate',
    'apOverview.importOutcomeViewed':
      'importId uploadAttemptId presentationScreenId outcome createdCount countsComplete',
    'apOverview.attemptUnresolved':
      'operation reason elapsedMs requestId billId uploadAttemptId uploadBatchId importId',
    'apOverview.errorObserved':
      'operation errorClass errorCode errorId requestId billId uploadAttemptId uploadBatchId importId',
    'apOverview.visitSummary':
      'summaryRevision elapsedMs activeMs lastAction exitReason destinationId',
    'apOverview.supportEntryClicked': 'entryPoint destinationId',
    'apOverview.supportSearchCommitted':
      'hasQuery queryLengthBucket resultCount',
    'apOverview.supportMessageEntryClicked': 'entryPoint'
  },
  enums: {
    visitType: ['first', 'return'],
    entrySource: ['sidebar', 'direct', 'other_screen', 'refresh'],
    screenState: screenStates,
    sectionId: overviewSections,
    availableSections: overviewSections,
    unavailableSections: overviewSections,
    state: [...sectionStates, 'open', 'closed', 'opened', 'canceled'],
    previousState: sectionStates,
    reason: [
      'trigger',
      'dismiss',
      'selection',
      'valid',
      'unsupported_type',
      'too_large',
      'capacity',
      'pay_date_invalid',
      'funding_floor_unavailable',
      'timeout',
      'connection_lost'
    ],
    availableActions: overviewActions,
    action: overviewActions,
    destinationId: overviewDestinations,
    billState: billStates,
    sourceSection: ['upcoming_bills', 'approvals'],
    outcome: [
      'success',
      'failure',
      'accepted',
      'rejected',
      'deferred',
      'created',
      'partial',
      'no_new_bills',
      'failed'
    ],
    selectionMethod: ['picker', 'drop'],
    fileType: ['pdf', 'png', 'jpeg', 'unsupported'],
    sizeBucket: ['under_1_mb', '1_to_5_mb', 'over_5_to_10_mb', 'over_10_mb'],
    presentationScreenId: ['ap_overview', 'bills_list', 'other_screen'],
    operation: operations,
    errorClass: errorClasses,
    errorCode: [
      'validation',
      'unsupported_type',
      'too_large',
      'capacity',
      'permission_denied',
      'state_conflict',
      'not_found',
      'network',
      'timeout',
      'service_unavailable',
      'persistence_failed',
      'queue_failed',
      'extraction_failed',
      'clipboard_denied',
      'unknown'
    ],
    lastAction: lastActions,
    exitReason: ['navigation', 'hidden', 'closed', 'unknown'],
    entryPoint: ['widget', 'help_center'],
    queryLengthBucket: ['1_to_10', '11_to_30', 'over_30']
  }
})

registerApLegacyEvents({
  submitBillForApprovalSuccess: 'billID actionAttemptId operationId requestId',
  zendesk_support_chat_opened: 'entryPoint screenId actionAttemptId'
})

let current: ApContext | undefined
let lastAction: string | undefined
let lastDestination: string | undefined
let sidebarEntry = false
let nextEntry: 'sidebar' | 'other_screen' | undefined
let restoredFromCache = false
let visitStartedAt = 0

export const overviewAnalytics = {
  bind: (context: ApContext | undefined) => {
    if (context?.screenVisitId !== current?.screenVisitId) {
      visitStartedAt = Date.now()
      resetOverviewSections()
    }
    current = context
    if (!context) {
      lastAction = undefined
      lastDestination = undefined
      restoredFromCache = false
    }
  },
  context: () => current,
  attempt: (intents?: ApIntents) => apAnalytics.attempt(current, intents),
  note: (action: string, destination?: string) => {
    lastAction = action
    if (destination) lastDestination = destination
  },
  lastAction: () => lastAction,
  lastDestination: () => lastDestination,
  markRestored: () => {
    restoredFromCache = true
  },
  restored: () => restoredFromCache
}

export const rememberOverviewSidebarEntry = (destination: unknown) => {
  sidebarEntry =
    typeof destination === 'string' &&
    destination.split(/[?#]/)[0] === '/dashboard/accounts-payable'
}

export const rememberOverviewNavigationEntry = (from: string, to: string) => {
  const fromPath = from.split(/[?#]/)[0]
  const toPath = to.split(/[?#]/)[0]
  if (toPath !== '/dashboard/accounts-payable' || fromPath === toPath) {
    sidebarEntry = false
    return
  }
  nextEntry = sidebarEntry ? 'sidebar' : 'other_screen'
  sidebarEntry = false
}

export const takeOverviewEntrySource = ():
  | 'sidebar'
  | 'direct'
  | 'other_screen'
  | 'refresh' => {
  if (nextEntry) {
    const source = nextEntry
    nextEntry = undefined
    return source
  }
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    if (nav?.type === 'reload') return 'refresh'
    if (nav?.type === 'back_forward') return 'other_screen'
  } catch {
    /* Direct when the navigation type is unavailable. */
  }
  return 'direct'
}

export const overviewBillState = (
  status: string | null | undefined
): (typeof billStates)[number] => {
  switch (status) {
    case 'BILL_DRAFT':
    case 'draft':
      return 'draft'
    case 'BILL_NEED_APPROVAL':
    case 'needs_approval':
      return 'needs_approval'
    case 'BILL_SCHEDULED':
    case 'scheduled':
      return 'scheduled'
    case 'BILL_PROCESSING':
    case 'processing':
      return 'processing'
    case 'BILL_PAID':
    case 'BILL_PAID_EXTERNALLY':
    case 'paid':
      return 'paid'
    case 'BILL_FAILED':
    case 'failed':
      return 'failed'
    default:
      return 'unknown'
  }
}

export const overviewDestination = (href: string): OverviewDestination => {
  try {
    const url = new URL(href, 'https://overview.local')
    const path = url.pathname
    if (path.endsWith('/bills/create')) return 'bill_create'
    if (path.includes('/vendors/duplicates')) return 'vendor_duplicates'
    if (path.endsWith('/pay-one') || path.includes('/pay-one'))
      return 'payment_review'
    if (/\/vendors\/[^/]+$/.test(path)) return 'vendor_details'
    if (path.endsWith('/vendors')) return 'vendors'
    if (url.searchParams.has('editPayDate')) return 'pay_date_editor'
    if (path.includes('/bills/') && path.endsWith('/details'))
      return 'bill_details'
    if (/\/bills\/[^/]+$/.test(path)) return 'bill_details'
    if (path.endsWith('/bills')) {
      const tab = url.searchParams.get('tab')
      if (tab === 'scheduled') return 'bills_scheduled'
      if (tab === 'needs_approval') return 'bills_needs_approval'
      return 'bills'
    }
  } catch {
    /* Unparseable href stays other_product. */
  }
  return 'other_product'
}

export const overviewScreenState = (
  states: OverviewSectionState[]
): OverviewScreenState => {
  const settled = states.filter(
    state => state !== 'loading' && state !== 'unavailable'
  )
  const errors = settled.filter(state => state === 'error').length
  if (settled.length === 0) return errors ? 'error' : 'empty'
  if (errors === settled.length) return 'error'
  if (errors > 0) return 'partial'
  return settled.some(state => state === 'populated') ? 'populated' : 'empty'
}

export const overviewErrorClass = (
  code: ApErrorCode | string
): (typeof errorClasses)[number] => {
  if (
    code === 'validation' ||
    code === 'unsupported_type' ||
    code === 'too_large' ||
    code === 'capacity' ||
    code === 'clipboard_denied'
  )
    return 'validation'
  if (code === 'permission_denied') return 'permission'
  if (code === 'state_conflict' || code === 'not_found' || code === 'duplicate')
    return 'conflict'
  if (code === 'network') return 'network'
  if (
    code === 'timeout' ||
    code === 'service_unavailable' ||
    code === 'persistence_failed' ||
    code === 'queue_failed' ||
    code === 'extraction_failed'
  )
    return 'service'
  return 'unknown'
}

const handoffKey = (destinationId: string) => `overview:${destinationId}`

export const trackOverviewAction = (
  properties: Properties & { destinationId?: string; href?: string }
) => {
  const attempt = overviewAnalytics.attempt()
  if (properties.action)
    overviewAnalytics.note(String(properties.action), properties.destinationId)
  apAnalytics.emit('apOverview.actionClicked', properties, attempt)
  if (attempt && properties.destinationId)
    rememberApHandoff(handoffKey(String(properties.destinationId)), attempt, {
      ...properties,
      href: properties.href
    })
  return attempt
}

export const completeOverviewNavigation = (
  destinationId: string,
  accountId: string,
  destinationVisitId: string,
  ids: { billId?: string; vendorId?: string } = {}
) => {
  const handoff = takeApHandoff(
    handoffKey(destinationId),
    accountId,
    properties => properties.destinationId === destinationId
  )
  if (!handoff) return
  apAnalytics.emit(
    'apOverview.navigationCompleted',
    {
      destinationId,
      destinationVisitId,
      durationMs: handoff.elapsedMs,
      sectionId: handoff.properties.sectionId,
      billId: ids.billId ?? handoff.properties.billId,
      vendorId: ids.vendorId ?? handoff.properties.vendorId
    },
    handoff.attempt,
    `nav:${destinationId}:${destinationVisitId}`
  )
}

export const overviewNavigationFailed = (error: unknown, target: string) => {
  if (
    error &&
    typeof error === 'object' &&
    'cancelled' in error &&
    error.cancelled === true
  )
    return
  const destinationId = overviewDestination(target)
  const accountId = current?.accountId
  if (!accountId) return
  const handoff = takeApHandoff(
    handoffKey(destinationId),
    accountId,
    properties =>
      properties.href === target || properties.destinationId === destinationId
  )
  if (!handoff) return
  observeOverviewFailure('navigation', error, handoff.attempt, {})
}

export const observeOverviewFailure = (
  operation: (typeof operations)[number],
  error: unknown,
  attempt: ApAttempt | undefined,
  ids: Properties
) => {
  const { errorId, errorCode } = apAnalytics.errorIds(error)
  apAnalytics.emit(
    'apOverview.errorObserved',
    {
      operation,
      errorClass: overviewErrorClass(errorCode),
      errorCode,
      errorId,
      ...ids
    },
    attempt ?? current,
    `error:${errorId}`
  )
  if (errorCode === 'timeout' || errorCode === 'network')
    apAnalytics.emit(
      'apOverview.attemptUnresolved',
      {
        operation,
        reason: errorCode === 'timeout' ? 'timeout' : 'connection_lost',
        elapsedMs: attempt ? Date.now() - attempt.startedAt : 0,
        ...ids
      },
      attempt,
      `unresolved:${attempt?.actionAttemptId ?? errorId}`
    )
  return { errorId, errorCode }
}

export const overviewSupportQuery = (query: string, resultCount?: number) => {
  const attempt = overviewAnalytics.attempt()
  overviewAnalytics.note('support')
  apAnalytics.emit(
    'apOverview.supportSearchCommitted',
    {
      hasQuery: query.length > 0,
      queryLengthBucket:
        apLengthBucket(query) === 'empty' ? undefined : apLengthBucket(query),
      resultCount: resultCount ?? null
    },
    attempt
  )
}
interface Load {
  key: string
  id: string
  startedAt: number
}
const loads = new Map<string, Load>()
interface SectionMemory {
  state: OverviewSectionState
  loadKey: string
  itemCount: number | null
  errorId?: string
}
const sections = new Map<string, SectionMemory>()

export const resetOverviewSections = () => {
  loads.clear()
  sections.clear()
}

export const overviewLoadAttempt = (sectionId: string, loadKey: string) => {
  const existing = loads.get(sectionId)
  if (existing?.key === loadKey) return existing
  const created = { key: loadKey, id: uuid(), startedAt: Date.now() }
  loads.set(sectionId, created)
  return created
}

export const trackOverviewPeriod = (
  sectionId:
    | 'performance'
    | 'spend_by_category'
    | 'payment_method_usage'
    | 'top_vendors',
  previous: number,
  next: number
) => {
  if (previous === next) return
  const load = overviewLoadAttempt(sectionId, String(next))
  const attempt = overviewAnalytics.attempt()
  overviewAnalytics.note('period_changed')
  apAnalytics.emit(
    'apOverview.periodChanged',
    {
      sectionId,
      previousPeriodDays: previous,
      periodDays: next,
      loadAttemptId: load.id
    },
    attempt
  )
  return load
}

export interface OverviewSectionInput {
  sectionId: (typeof overviewDataSections)[number]
  loadKey?: string
  loading: boolean
  hasData: boolean
  error?: unknown
  itemCount: number | null
  countsComplete?: boolean
  unavailable?: boolean
}

export const observeOverviewSection = (input: OverviewSectionInput) => {
  if (!current) return undefined
  const loadKey = input.loadKey ?? 'initial'
  const load = overviewLoadAttempt(input.sectionId, loadKey)
  const previous = sections.get(input.sectionId)
  const loadChanged = previous != null && previous.loadKey !== loadKey
  let state: OverviewSectionState = 'loading'
  if (input.unavailable) state = 'unavailable'
  else if (input.error) state = 'error'
  else if (!input.hasData) state = 'loading'
  else if (input.loading && loadChanged) state = 'loading'
  else if (input.itemCount === 0) state = 'empty'
  else state = 'populated'
  const errorIds = input.error ? apAnalytics.errorIds(input.error) : undefined
  if (
    previous &&
    previous.state === state &&
    previous.loadKey === loadKey &&
    previous.itemCount === input.itemCount &&
    previous.errorId === errorIds?.errorId
  )
    return undefined
  if (
    !previous &&
    input.hasData &&
    input.loading &&
    input.sectionId !== 'quick_actions'
  )
    overviewAnalytics.markRestored()
  const report = {
    sectionId: input.sectionId,
    state,
    previousState: previous?.state ?? null,
    loadAttemptId: load.id,
    itemCount: state === 'loading' ? null : input.itemCount,
    durationMs: Date.now() - load.startedAt,
    countsComplete: input.countsComplete !== false && input.itemCount != null,
    ...(errorIds ?? {})
  }
  sections.set(input.sectionId, {
    state,
    loadKey,
    itemCount: input.itemCount,
    errorId: errorIds?.errorId
  })
  if (current)
    apAnalytics.emit(
      'apOverview.sectionStateChanged',
      report,
      current,
      `${input.sectionId}:${load.id}:${state}:${input.itemCount ?? 'none'}`
    )
  if (state === 'error' && input.error)
    observeOverviewFailure('section_load', input.error, undefined, {
      loadAttemptId: load.id
    })
  return report
}

export const overviewSectionStates = () =>
  overviewDataSections.map(
    sectionId => sections.get(sectionId)?.state ?? 'loading'
  )

export const emitOverviewScreenViewed = () => {
  if (!current) return
  const states = overviewDataSections
    .filter(sectionId => sectionId !== 'quick_actions')
    .map(sectionId => sections.get(sectionId)?.state ?? 'loading')
  if (states.some(state => state === 'loading')) return
  const unavailable = overviewDataSections.filter(
    sectionId => sections.get(sectionId)?.state === 'unavailable'
  )
  const available = overviewSections.filter(
    section =>
      !unavailable.includes(section as (typeof overviewDataSections)[number])
  )
  const prior = recordApVisit(current)
  let restored = overviewAnalytics.restored()
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    if (nav?.type === 'back_forward') restored = true
  } catch {
    /* Restoration stays unknown rather than invented. */
  }
  apAnalytics.emit(
    'apOverview.screenViewed',
    {
      visitType: prior.priorScreenVisitId ? 'return' : 'first',
      entrySource: takeOverviewEntrySource(),
      loadDurationMs: Date.now() - visitStartedAt,
      screenState: overviewScreenState(states),
      availableSections: available,
      unavailableSections: unavailable,
      restoredState: restored
    },
    current,
    'view'
  )
}

export interface OverviewUploadBatch {
  uploadBatchId: string
  attempt: ApAttempt
  uploadAttemptIds: string[]
}

export const beginOverviewUpload = (
  selectionMethod: 'picker' | 'drop'
): OverviewUploadBatch | undefined => {
  const uploadBatchId = uuid()
  const attempt = overviewAnalytics.attempt({ uploadBatchId })
  if (!attempt) return undefined
  overviewAnalytics.note('upload')
  apAnalytics.emit(
    'apOverview.uploadSelectionStarted',
    { uploadBatchId, selectionMethod },
    {
      ...attempt,
      inputMethod: selectionMethod === 'drop' ? 'drop' : attempt.inputMethod
    }
  )
  return { uploadBatchId, attempt, uploadAttemptIds: [] }
}

export const cancelOverviewUpload = (batch: OverviewUploadBatch) => {
  apAnalytics.emit(
    'apOverview.uploadSelectionCanceled',
    { uploadBatchId: batch.uploadBatchId },
    batch.attempt
  )
}

export const selectOverviewFiles = (
  batch: OverviewUploadBatch,
  files: {
    fileType: string
    sizeBucket: string
    outcome: string
    reason: string
  }[]
) => {
  batch.uploadAttemptIds = files.map(() => uuid())
  let acceptedCount = 0
  let rejectedCount = 0
  let capacityDeferredCount = 0
  files.forEach((file, index) => {
    if (file.outcome === 'accepted') acceptedCount += 1
    else if (file.outcome === 'rejected') rejectedCount += 1
    else capacityDeferredCount += 1
    apAnalytics.emit(
      'apOverview.uploadFileValidationCompleted',
      {
        uploadBatchId: batch.uploadBatchId,
        uploadAttemptId: batch.uploadAttemptIds[index],
        fileType: file.fileType,
        sizeBucket: file.sizeBucket,
        outcome: file.outcome,
        reason: file.reason
      },
      batch.attempt
    )
  })
  apAnalytics.emit(
    'apOverview.uploadFilesSelected',
    {
      uploadBatchId: batch.uploadBatchId,
      uploadAttemptIds: batch.uploadAttemptIds,
      selectedCount: files.length,
      acceptedCount,
      rejectedCount,
      capacityDeferredCount
    },
    batch.attempt
  )
}

export const dispatchOverviewUpload = (
  requestId: string,
  attempt: ApAttempt,
  file: {
    uploadBatchId?: string
    uploadAttemptId?: string
    fileType?: string
    sizeBucket?: string
  }
) => {
  apAnalytics.emit(
    'apOverview.uploadDispatched',
    { requestId, ...file },
    attempt
  )
}

export const unresolvedOverviewImport = (
  attempt: ApAttempt,
  importId: string,
  uploadAttemptId: string
) => {
  apAnalytics.emit(
    'apOverview.attemptUnresolved',
    {
      operation: 'import_acceptance',
      reason: 'connection_lost',
      importId,
      uploadAttemptId,
      elapsedMs: Date.now() - attempt.startedAt
    },
    attempt,
    `import-unresolved:${importId}`
  )
}

export const overviewPresentationScreen = ():
  | 'ap_overview'
  | 'bills_list'
  | 'other_screen' => {
  if (current) return 'ap_overview'
  if (typeof window === 'undefined') return 'other_screen'
  if (window.location.pathname === '/dashboard/accounts-payable/bills')
    return 'bills_list'
  if (window.location.pathname === '/dashboard/accounts-payable')
    return 'ap_overview'
  return 'other_screen'
}

export const viewOverviewImport = (
  attempt: ApAttempt,
  imported: {
    importId: string
    uploadAttemptId: string
    outcome: string
    createdCount: number | null
    countsComplete: boolean
  }
) => {
  apAnalytics.emit(
    'apOverview.importOutcomeViewed',
    {
      ...imported,
      presentationScreenId: overviewPresentationScreen()
    },
    attempt,
    `import:${imported.importId}:${imported.outcome}:${
      imported.createdCount ?? 'unknown'
    }`
  )
}
